import { Injectable } from '@angular/core';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    AirspaceAuthorisationService,
    RequestAirspaceAuthorisationCommand
} from '@flyfreely-portal-ui/flyfreely';
import { EMPTY, Observable, map } from 'rxjs';
import { AlternativeAirspaceAuthorisationRequestCommand } from './interfaces';
import { AirspaceAuthorisationProvider } from './airspace-authorisation-provider';

/**
 * Routes all authorisation operations through the standard FlyFreely REST API
 * using Angular's normal HTTP interceptor chain (authentication, error
 * parsing, etc.).
 *
 * `delete`, `close`, and `acknowledgeRescinded` are not yet implemented in
 * the standard API and return `EMPTY`. They are only available via
 * `ExternalAuthorisationProvider`.
 */
@Injectable()
export class FlyFreelyAuthorisationProvider
    implements AirspaceAuthorisationProvider
{
    constructor(
        private airspaceAuthorisationService: AirspaceAuthorisationService
    ) {}

    getPreview(
        command:
            | RequestAirspaceAuthorisationCommand
            | AlternativeAirspaceAuthorisationRequestCommand
    ): Observable<AirspaceAuthorisationResponse> {
        return this.airspaceAuthorisationService.getAuthorisationPreview(
            command as RequestAirspaceAuthorisationCommand
        );
    }

    apply(
        command:
            | RequestAirspaceAuthorisationCommand
            | AlternativeAirspaceAuthorisationRequestCommand
    ): Observable<AirspaceAuthorisationDto[]> {
        return this.airspaceAuthorisationService.applyForAuthorisation(
            command as RequestAirspaceAuthorisationCommand
        );
    }

    findById(
        id: string,
        type?: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<AirspaceAuthorisationDto | null> {
        // The standard API returns an array; normalize to the first element.
        return this.airspaceAuthorisationService
            .findAuthorisation(id, type)
            .pipe(map(list => (list?.length > 0 ? list[0] : null)));
    }

    cancel(
        id: string,
        type?: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<AirspaceAuthorisationDto> {
        return this.airspaceAuthorisationService.cancelAuthorisation(id, type);
    }

    // Not yet implemented in the standard FlyFreely API.
    delete(
        _id: string,
        _type?: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<void> {
        return EMPTY;
    }

    close(
        id: string,
        type?: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<AirspaceAuthorisationDto> {
        return this.airspaceAuthorisationService.closeAuthorisation(id, type);
    }

    // Not yet implemented in the standard FlyFreely API.
    acknowledgeRescinded(
        _id: string,
        _type?: AirspaceAuthorisationDto.AuthorisationType
    ): Observable<AirspaceAuthorisationDto> {
        return EMPTY;
    }
}
